import FieldInfo from '@/components/form/FieldInfo'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useId, useState } from 'react'
import CollectionStorefrontVisibilityIndicator, {
  getCollectionStorefrontVisibilityMessage,
} from './CollectionStorefrontVisibilityIndicator'
import {
  COLLECTION_STOREFRONT_VISIBILITY_ISSUE,
  getCollectionStorefrontVisibilityIssue,
} from './designForm.utils'
import {
  findNavigationCollectionItem,
  navigationPlacementLabel,
} from './navigationMenuEditor.utils'

function CollectionOption({
  collection,
  items,
  selected,
  tooltipOpen,
  onSelect,
  onTooltipOpenChange,
}) {
  const location = findNavigationCollectionItem(items, collection.id)
  const visibilityIssue = getCollectionStorefrontVisibilityIssue(collection)
  const visibilityMessage =
    visibilityIssue === COLLECTION_STOREFRONT_VISIBILITY_ISSUE.HIDDEN
      ? 'This collection is currently hidden.'
      : visibilityIssue
        ? getCollectionStorefrontVisibilityMessage(visibilityIssue)
        : null
  const locationLabel = location ? navigationPlacementLabel(items, location.parentId) : null
  const status = [
    locationLabel,
    visibilityIssue === COLLECTION_STOREFRONT_VISIBILITY_ISSUE.HIDDEN ? 'Hidden' : null,
    visibilityIssue === COLLECTION_STOREFRONT_VISIBILITY_ISSUE.NO_AVAILABLE_PRODUCTS
      ? 'No visible products'
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const renderOption = (visibilityIndicator) => (
    <CommandItem
      value={`${collection.name} ${status || 'Available'} ${collection.id}`}
      className="items-start py-2"
      onPointerEnter={() => visibilityMessage && onTooltipOpenChange(true)}
      onPointerLeave={() => onTooltipOpenChange(false)}
      onFocus={() => visibilityMessage && onTooltipOpenChange(true)}
      onBlur={() => onTooltipOpenChange(false)}
      onSelect={() => onSelect(collection.id)}
    >
      <Check className={cn('mt-0.5 size-4', selected ? 'opacity-100' : 'opacity-0')} />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-2 font-medium">
          <span className="truncate">{collection.name}</span>
          {visibilityIndicator}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {status || 'Available'}
        </span>
      </span>
    </CommandItem>
  )

  const visibilityIndicator = visibilityIssue ? (
    <CollectionStorefrontVisibilityIndicator
      visibilityIssue={visibilityIssue}
      withTooltip={false}
      message={visibilityMessage}
    />
  ) : null

  if (!visibilityMessage) return renderOption(visibilityIndicator)

  return (
    <Tooltip open={tooltipOpen}>
      {renderOption(
        <TooltipTrigger asChild>
          <span className="inline-flex">{visibilityIndicator}</span>
        </TooltipTrigger>
      )}
      <TooltipContent
        side="top"
        className="pointer-events-none w-61"
        style={{ pointerEvents: 'none' }}
        variant="warning"
      >
        {visibilityMessage}
      </TooltipContent>
    </Tooltip>
  )
}

export default function NavigationCollectionPicker({ field, collections, items }) {
  const [open, setOpen] = useState(false)
  const [tooltipCollectionId, setTooltipCollectionId] = useState(null)
  const generatedId = useId()
  const triggerId = `${generatedId}-${field.name}`
  const selectedCollection = collections.find(
    (collection) => Number(collection.id) === Number(field.state.value)
  )

  return (
    <div className="flex flex-col gap-y-2">
      <Label htmlFor={triggerId}>Collection</Label>
      <Popover
        modal
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen)
          if (!nextOpen) setTooltipCollectionId(null)
        }}
      >
        <PopoverTrigger asChild>
          <Button
            id={triggerId}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            <span className={cn('truncate', !selectedCollection && 'text-muted-foreground')}>
              {selectedCollection?.name ?? 'Choose a collection'}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          size="trigger"
          padding="none"
          onKeyDownCapture={(event) => {
            if (event.key === 'Escape') setTooltipCollectionId(null)
          }}
        >
          <Command>
            <CommandInput placeholder="Search collections..." />
            <CommandList>
              <CommandEmpty>No collections found.</CommandEmpty>
              <CommandGroup>
                {collections.map((collection) => (
                  <CollectionOption
                    key={collection.id}
                    collection={collection}
                    items={items}
                    selected={Number(collection.id) === Number(field.state.value)}
                    tooltipOpen={Number(tooltipCollectionId) === Number(collection.id)}
                    onTooltipOpenChange={(nextOpen) =>
                      setTooltipCollectionId((currentId) => {
                        if (nextOpen) return collection.id
                        return Number(currentId) === Number(collection.id) ? null : currentId
                      })
                    }
                    onSelect={(collectionId) => {
                      field.handleChange(Number(collectionId))
                      field.handleBlur()
                      setOpen(false)
                    }}
                  />
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <FieldInfo />
    </div>
  )
}
